import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import axios from 'axios';
import {
  Buildings,
  House,
  Users,
  FileText,
  Wallet,
  Gear,
  Bell,
  SignOut,
  List,
  X,
  Key,
  ChartBar,
  Money,
  Table,
  Percent,
  Gift,
  CaretDown,
  UsersThree,
  ShieldCheck,
  Tag,
  Database,
  Target,
  ChartLineUp,
  Handshake,
  Warning,
  TreeStructure,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SpinnerGap, PencilSimple, ClockClockwise } from '@phosphor-icons/react';
import { TimeZoneNotesWidget } from '../components/TimeZoneNotesWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = () => {
  const { user, logout, isSuperadmin, isAdmin, isManager, isLead, canManageUsers } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications?unread_only=true`, {
        withCredentials: true,
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`, {}, { withCredentials: true });
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openProfileEdit = () => {
    setProfileForm({
      first_name: user?.name?.split(' ')[0] || '',
      last_name: user?.name?.split(' ').slice(1).join(' ') || '',
      designation: user?.designation || '',
      direct_number: user?.direct_number || '',
    });
    setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    setProfileMsg('');
    setPasswordMsg('');
    setShowProfileModal(true);
  };

  const openPasswordChange = () => {
    setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    setPasswordMsg('');
    setShowPasswordModal(true);
  };

  const handleProfileSave = async () => {
    setProfileLoading(true); setProfileMsg('');
    try {
      await axios.put(`${API_URL}/api/users/${user?.id}`, profileForm, { withCredentials: true });
      setProfileMsg('Profile updated!');
      setTimeout(() => { setShowProfileModal(false); window.location.reload(); }, 1000);
    } catch (err) { setProfileMsg('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setProfileLoading(false); }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg('');
    const { old_password, new_password, confirm_password } = passwordForm;
    if (!old_password || !new_password || !confirm_password) {
      setPasswordMsg('Error: All password fields are required');
      return;
    }
    if (new_password.length < 6) {
      setPasswordMsg('Error: New password must be at least 6 characters');
      return;
    }
    if (new_password !== confirm_password) {
      setPasswordMsg('Error: New password and confirm password do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { old_password, new_password },
        { withCredentials: true },
      );
      setPasswordMsg('Password updated successfully!');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setShowPasswordModal(false), 1200);
    } catch (err) {
      setPasswordMsg('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPasswordLoading(false);
    }
  };

  const { canAccessPage } = useRBAC();

  const navItems = [
    { path: '/dashboard', icon: House, label: 'Dashboard', roles: 'all', pageKey: 'dashboard' },
    { path: '/dashboard/ceo', icon: ChartBar, label: 'Executive Dashboard', roles: ['ceo'], pageKey: 'ceo_dashboard' },
    { path: '/dashboard/placements', icon: FileText, label: 'Placements', roles: ['sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'sales_assistant_manager', 'recruiting_assistant_manager', 'ceo', 'superadmin'], pageKey: 'placements' },
    { path: '/dashboard/review-queue', icon: List, label: 'Review Queue', roles: ['sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'onsite_manager', 'superadmin', 'ceo'], pageKey: 'review_queue' },
    { path: '/dashboard/supported-placements', icon: Handshake, label: 'Supported Placements', roles: 'all', pageKey: 'supported_placements' },
    { path: '/dashboard/backouts', icon: Warning, label: 'Backouts', roles: 'all', pageKey: 'backouts' },
    { path: '/dashboard/earnings', icon: Wallet, label: 'My Earnings', roles: ['sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'onsite_manager', 'sales_assistant_manager', 'recruiting_assistant_manager'], pageKey: 'earnings' },
    {
      path: '/dashboard/role-incentives',
      icon: Money,
      label: ['sales_lead', 'recruiting_lead'].includes(user?.role) ? 'Lead Incentive' : 'Manager Incentive',
      roles: ['sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'sales_assistant_manager', 'recruiting_assistant_manager'],
      pageKey: 'role_incentives',
    },
    { path: '/dashboard/reporting-structure', icon: TreeStructure, label: 'Org Structure', roles: 'all', pageKey: 'reporting_structure' },
    { path: '/dashboard/organization-chart', icon: TreeStructure, label: 'Organization Chart', roles: 'all', pageKey: 'organization_chart' },
  ];

  const managerItems = [
    { path: '/dashboard/team', icon: Users, label: 'Team Overview', roles: ['sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'onsite_manager', 'superadmin', 'ceo'], pageKey: 'team_overview' },
  ];

  const adminItems = [
    { path: '/dashboard/admin/users', icon: Users, label: 'Employee Directory', roles: ['superadmin', 'ceo', 'people_admin'], pageKey: 'user_directory' },
    { path: '/dashboard/admin/teams', icon: UsersThree, label: 'Teams & Analytics', roles: ['superadmin', 'ceo', 'people_admin'], pageKey: 'teams_analytics' },
    { path: '/dashboard/admin/vendor-matrix', icon: Table, label: 'Vendor Matrix', roles: ['superadmin', 'ceo'], pageKey: 'vendor_matrix' },
    { path: '/dashboard/admin/incentive-matrix', icon: Money, label: 'Incentive Matrix', roles: ['superadmin', 'ceo'], pageKey: 'incentive_matrix' },
    { path: '/dashboard/admin/organizations', icon: Buildings, label: 'Organizations', roles: ['superadmin', 'ceo'], pageKey: 'organizations' },
    { path: '/dashboard/admin/bonus-rules', icon: Gift, label: 'Bonus Rules', roles: ['superadmin', 'ceo'], pageKey: 'bonus_rules' },
    { path: '/dashboard/admin/vendor-claims', icon: Handshake, label: 'Vendor Claims', roles: ['superadmin', 'ceo'], pageKey: 'vendor_claims' },
    { path: '/dashboard/admin/discounts', icon: Tag, label: 'Discounts', roles: ['superadmin', 'ceo'], pageKey: 'discounts' },
    { path: '/dashboard/admin/entities', icon: Database, label: 'Orgs/Vendors/Clients/C2C', roles: ['superadmin', 'ceo'], pageKey: 'companies_vendors' },
    { path: '/dashboard/admin/targets', icon: Target, label: 'Targets & Bonuses', roles: ['superadmin', 'ceo'], pageKey: 'targets_bonuses' },
    { path: '/dashboard/admin/bonus-tracker', icon: ChartLineUp, label: 'Bonus Tracker', roles: ['superadmin', 'ceo'], pageKey: 'bonus_tracker' },
    { path: '/dashboard/admin/access-control', icon: ShieldCheck, label: 'Access Control', roles: ['superadmin', 'ceo'], pageKey: 'access_control' },
    { path: '/dashboard/admin/payroll', icon: ChartBar, label: 'Payroll Export', roles: ['superadmin', 'ceo'], pageKey: 'payroll_export' },
    { path: '/dashboard/admin/custom-fields', icon: Tag, label: 'Custom Fields', roles: ['superadmin', 'ceo'], pageKey: 'custom_fields' },
    { path: '/dashboard/admin/audit-log', icon: ClockClockwise, label: 'Audit Log', roles: ['superadmin', 'ceo'], pageKey: 'audit_log' },
    { path: '/dashboard/reports', icon: ChartBar, label: 'Reports', roles: ['superadmin', 'ceo', 'sales_manager', 'recruiting_manager', 'sales_lead', 'recruiting_lead', 'sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'onsite_manager'], pageKey: 'reports' },
  ];

  const isActiveRoute = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const canAccess = (roles, pageKey) => {
    // First check role-based access
    const roleAllowed = roles === 'all' ? true : roles.includes(user?.role);
    if (!roleAllowed) return false;
    // Then check RBAC page visibility
    if (pageKey && !canAccessPage(pageKey)) return false;
    return true;
  };

  const visibleAdminItems = adminItems.filter((item) => canAccess(item.roles, item.pageKey));

  const getRoleLabel = (role) => {
    const labels = {
      ceo: 'CEO',
      superadmin: 'Super Admin',
      people_admin: 'People Admin',
      sales_recruiter: 'Sales Recruiter',
      sales_technical_recruiter: 'Sales Recruiter & Technical Recruiter',
      sales_lead: 'Sales Lead',
      sales_manager: 'Sales Manager',
      sales_assistant_manager: 'Sales Asst. Manager',
      technical_recruiter: 'Technical Recruiter',
      recruiting_lead: 'Recruiting Lead',
      recruiting_manager: 'Recruiting Manager',
      recruiting_assistant_manager: 'Recruiting Asst. Manager',
      onsite_manager: 'Onsite Manager',
    };
    return labels[role] || role?.replace(/_/g, ' ');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <img src="/intellectt_logo.png" alt="Intellectt" className="h-12 object-contain" />
            </div>
            <button className="lg:hidden text-slate-300 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Main</div>
            {navItems.filter((item) => canAccess(item.roles, item.pageKey)).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} weight={isActiveRoute(item.path) ? 'fill' : 'regular'} />
                {item.label}
              </Link>
            ))}

            {(isLead || isManager || isAdmin || canManageUsers) && (
              <>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Management</div>
                {managerItems.filter((item) => canAccess(item.roles, item.pageKey)).map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon size={20} weight={isActiveRoute(item.path) ? 'fill' : 'regular'} />
                    {item.label}
                  </Link>
                ))}
              </>
            )}

            {visibleAdminItems.length > 0 && (
              <>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Administration</div>
                {visibleAdminItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon size={20} weight={isActiveRoute(item.path) ? 'fill' : 'regular'} />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.designation || getRoleLabel(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <List size={24} />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="p-2 font-semibold border-b">Notifications</div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500 text-center">No new notifications</div>
                  ) : (
                    <>
                      {notifications.slice(0, 5).map((notif, idx) => (
                        <DropdownMenuItem key={idx} className="p-3 cursor-pointer">
                          <div>
                            <p className="text-sm">{notif.message}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(notif.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={markAsRead} className="justify-center text-blue-600">
                        Mark all as read
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden md:inline text-sm">{user?.name}</span>
                    <CaretDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openProfileEdit} data-testid="edit-profile-btn">
                    <PencilSimple size={16} className="mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openPasswordChange} data-testid="change-password-btn">
                    <Key size={16} className="mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <SignOut size={16} className="mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Profile Edit Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Your Profile</DialogTitle>
            <DialogDescription>Update your name, designation, contact number, or password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {profileMsg && <div className={`p-2 rounded text-sm ${profileMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{profileMsg}</div>}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">First Name</Label>
                <Input value={profileForm.first_name || ''} onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})} data-testid="profile-first-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name</Label>
                <Input value={profileForm.last_name || ''} onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})} data-testid="profile-last-name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Designation</Label>
              <Input value={profileForm.designation || ''} onChange={(e) => setProfileForm({...profileForm, designation: e.target.value})} placeholder="Sr. Recruiter" data-testid="profile-designation" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Direct Number</Label>
              <Input value={profileForm.direct_number || ''} onChange={(e) => setProfileForm({...profileForm, direct_number: e.target.value})} placeholder="+1 234-567-8900" data-testid="profile-phone" />
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-900">Change Password</p>
              {passwordMsg && <div className={`p-2 rounded text-sm ${passwordMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{passwordMsg}</div>}
              <div className="space-y-1">
                <Label className="text-xs">Old Password</Label>
                <Input type="password" value={passwordForm.old_password} onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })} data-testid="profile-old-password" autoComplete="current-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New Password</Label>
                <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} data-testid="profile-new-password" autoComplete="new-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirm Password</Label>
                <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} data-testid="profile-confirm-password" autoComplete="new-password" />
              </div>
              <Button variant="outline" className="w-full" onClick={handlePasswordChange} disabled={passwordLoading} data-testid="profile-change-password-btn">
                {passwordLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Update Password
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleProfileSave} disabled={profileLoading}>
              {profileLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {passwordMsg && <div className={`p-2 rounded text-sm ${passwordMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{passwordMsg}</div>}
            <div className="space-y-1">
              <Label className="text-xs">Old Password</Label>
              <Input type="password" value={passwordForm.old_password} onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })} data-testid="change-old-password" autoComplete="current-password" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New Password</Label>
              <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} data-testid="change-new-password" autoComplete="new-password" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Confirm Password</Label>
              <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} data-testid="change-confirm-password" autoComplete="new-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handlePasswordChange} disabled={passwordLoading} data-testid="change-password-submit">
              {passwordLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default DashboardLayout;
